/**
 * Received-video counters, read from the conference's active peer connection
 * (web and native: lib-jitsi-meet's TraceablePeerConnection.getStats).
 *
 * The Kuwait demo freeze (2026-09-27) showed 1.8 Mbps arriving at 0% loss
 * while the picture stood still, and the samples could not say whether the
 * decoder or the <video> element had stopped: nothing recorded what was drawn.
 * These counters feed the stall monitor (vrs-layout/remoteVideoStall.ts) and
 * the remote render fields of `media_quality_sample`.
 */
/** Counters older than this describe another moment of the call. */
export const REMOTE_RENDER_STALE_MS = 25000;
let latest;
let previousRefresh;
let baseline;
let refreshing;
/**
 * A finite number, or undefined.
 *
 * @param {unknown} value - A stats field.
 * @returns {number|undefined}
 */
function finite(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
/**
 * How much a counter grew since a baseline; a counter first seen counts in full.
 *
 * @param {number} [before] - The baseline reading.
 * @param {number} [after] - The current reading.
 * @returns {number|undefined}
 */
function grown(before, after) {
    if (after === undefined) {
        return undefined;
    }
    return Math.max(0, after - (before ?? 0));
}
/**
 * The received video counters in a stats report, keyed by the remote
 * MediaStreamTrack id. Browsers before inbound-rtp carried
 * `trackIdentifier` name it through the legacy `track` report.
 *
 * @param {IStatsReportLike} report - An RTCStatsReport (a Map on native).
 * @returns {Map<string, IInboundVideoCounters>}
 */
export function readInboundVideoCounters(report) {
    const legacyTrackIds = new Map();
    const counters = new Map();
    report.forEach(stat => {
        if (stat?.type === 'track' && typeof stat.id === 'string' && typeof stat.trackIdentifier === 'string') {
            legacyTrackIds.set(stat.id, stat.trackIdentifier);
        }
    });
    report.forEach(stat => {
        if (stat?.type !== 'inbound-rtp' || (stat.kind ?? stat.mediaType) !== 'video') {
            return;
        }
        const trackId = typeof stat.trackIdentifier === 'string'
            ? stat.trackIdentifier
            : legacyTrackIds.get(stat.trackId);
        if (!trackId) {
            return;
        }
        const next = {
            bytesReceived: finite(stat.bytesReceived),
            framesDecoded: finite(stat.framesDecoded),
            framesDropped: finite(stat.framesDropped),
            framesPerSecond: finite(stat.framesPerSecond),
            framesReceived: finite(stat.framesReceived),
            freezeCount: finite(stat.freezeCount),
            keyFramesDecoded: finite(stat.keyFramesDecoded),
            totalFreezesDuration: finite(stat.totalFreezesDuration)
        };
        const current = counters.get(trackId);
        // One entry per track; should a browser report two, keep the one
        // carrying the video.
        if (!current || (next.bytesReceived ?? 0) > (current.bytesReceived ?? 0)) {
            counters.set(trackId, next);
        }
    });
    return counters;
}
/**
 * The MediaStreamTrack id of a remote JitsiTrack, which inbound-rtp names.
 *
 * @param {Object} jitsiTrack - A JitsiRemoteTrack.
 * @returns {string|undefined}
 */
export function remoteMediaTrackId(jitsiTrack) {
    const track = typeof jitsiTrack?.getTrack === 'function' ? jitsiTrack.getTrack() : undefined;
    return typeof track?.id === 'string' ? track.id : undefined;
}
/**
 * Keeps one refresh of the counters for the summary.
 *
 * @param {Map} counters - Counters by remote track id.
 * @param {number} at - When they were read.
 * @returns {void}
 */
export function recordRemoteVideoCounters(counters, at) {
    previousRefresh = latest;
    latest = {
        at,
        counters
    };
}
/**
 * Reads the received video counters of the conference's active peer
 * connection, and keeps them for the telemetry summary. Concurrent callers
 * share one read.
 *
 * @param {Object} conference - The JitsiConference.
 * @returns {Promise<Map|undefined>} The counters, or undefined without a connection.
 */
export function refreshRemoteVideoCounters(conference) {
    const peerConnection = typeof conference?.getActivePeerConnection === 'function'
        ? conference.getActivePeerConnection()
        : undefined;
    if (typeof peerConnection?.getStats !== 'function') {
        return Promise.resolve(undefined);
    }
    if (refreshing) {
        return refreshing;
    }
    const read = Promise.resolve()
        .then(() => peerConnection.getStats())
        .then((report) => {
        const counters = readInboundVideoCounters(report);
        recordRemoteVideoCounters(counters, Date.now());
        return counters;
    })
        .catch(() => undefined)
        .finally(() => {
        if (refreshing === read) {
            refreshing = undefined;
        }
    });
    refreshing = read;
    return read;
}
/**
 * The counters of the latest refresh for one remote track.
 *
 * @param {string} trackId - Remote MediaStreamTrack id.
 * @param {number} now - Current time.
 * @returns {IInboundVideoCounters|undefined}
 */
export function latestRemoteVideoCounters(trackId, now) {
    return latest && now - latest.at <= REMOTE_RENDER_STALE_MS ? latest.counters.get(trackId) : undefined;
}
/**
 * Whether the counters were refreshed recently.
 *
 * @param {number} now - Current time.
 * @param {number} maxAgeMs - How old they may be.
 * @returns {boolean}
 */
export function remoteVideoCountersFresh(now, maxAgeMs) {
    return Boolean(latest && now - latest.at <= maxAgeMs);
}
/**
 * Remote render since the last emitted sample: frames decoded, freezes, and
 * the slowest remote video's frame rate. Undefined when no counters are
 * current.
 *
 * @param {number} now - Current time.
 * @returns {IRemoteRenderSummary|undefined}
 */
export function remoteRenderSummary(now) {
    if (!latest?.counters.size || now - latest.at > REMOTE_RENDER_STALE_MS) {
        return undefined;
    }
    const from = baseline && baseline !== latest ? baseline : previousRefresh;
    const seconds = from ? (latest.at - from.at) / 1000 : 0;
    let decoded;
    let freezes;
    let freezeSeconds;
    let slowest;
    latest.counters.forEach((current, trackId) => {
        const since = baseline?.counters.get(trackId);
        const trackDecoded = grown(since?.framesDecoded, current.framesDecoded);
        const trackFreezes = grown(since?.freezeCount, current.freezeCount);
        const trackFreezeSeconds = grown(since?.totalFreezesDuration, current.totalFreezesDuration);
        if (trackDecoded !== undefined) {
            decoded = (decoded ?? 0) + trackDecoded;
        }
        if (trackFreezes !== undefined) {
            freezes = (freezes ?? 0) + trackFreezes;
        }
        if (trackFreezeSeconds !== undefined) {
            freezeSeconds = (freezeSeconds ?? 0) + trackFreezeSeconds;
        }
        const was = from?.counters.get(trackId);
        if (seconds >= 1 && was?.framesDecoded !== undefined && current.framesDecoded !== undefined) {
            const fps = Math.max(0, current.framesDecoded - was.framesDecoded) / seconds;
            slowest = slowest === undefined ? fps : Math.min(slowest, fps);
        }
    });
    return {
        remoteFps: slowest === undefined ? undefined : Math.round(slowest * 10) / 10,
        remoteFramesDecodedDelta: decoded,
        remoteFreezeCount: freezes,
        remoteFreezeDurationMs: freezeSeconds === undefined ? undefined : Math.round(freezeSeconds * 1000)
    };
}
/**
 * The sample went out: the next summary counts from these counters.
 *
 * @returns {void}
 */
export function markRemoteRenderSampled() {
    baseline = latest;
}
/**
 * A new call starts from nothing.
 *
 * @returns {void}
 */
export function resetRemoteVideoCounters() {
    latest = undefined;
    previousRefresh = undefined;
    baseline = undefined;
    refreshing = undefined;
}
